// Shared query-param helpers for the /controlling/global endpoints
// (schedules list, pending count). Centralised here so the three
// Solution-Insights Controls widgets (AQM / Default / ESB) cannot drift
// again — the original cross-group leak was partly enabled by those
// widgets being copy-pasted and then diverging (ESB had silently lost
// the assetTypes param). Single source of truth removes the surface area
// for that class of bug.

// Single source of truth for "is this filter value meaningful enough to
// emit, parse, or compare?". Used by both the build side (omit-empty in
// the URL via appendIfPresent) and the parse / live side (normalise to
// "" for comparison via normaliseFilter + parseControllingGlobalArgs) so
// the symmetry that the arg-drift guard depends on is enforced by
// construction, not by convention — a future edit to one cannot desync
// the other.
//
// Predicate: present, non-empty string, and not one of the legacy
// literal artefacts ("null", "undefined") that arise when ${value} was
// interpolated into a template literal with a null/undefined input.
function isMeaningful(value) {
  if (value === undefined || value === null) return false;
  const s = String(value);
  return s !== "" && s !== "null" && s !== "undefined";
}

function appendIfPresent(qs, key, value) {
  if (isMeaningful(value)) qs.set(key, String(value));
}

// Build the params string for useGetSchedulesGlobalQuery.
// - All filter-like fields (serviceId, groupId, assetTypes) are routed
//   through appendIfPresent. Crucially that includes serviceId — a
//   missing serviceId emits no `serviceId=undefined` literal that would
//   then break the arg-drift guard's symmetry. The Controls widgets
//   should still gate the query at the React level when props.id is
//   unset (this is belt-and-braces).
// - `addGroupNames` is opt-in (AQM/Default render the groupName column;
//   ESB does not).
export function buildSchedulesParams({
  serviceId,
  page,
  groupId,
  assetTypes,
  addGroupNames,
}) {
  const qs = new URLSearchParams({
    pageSize: "10",
    currentPage: String(page),
    withTotalPages: "true",
  });
  appendIfPresent(qs, "serviceId", serviceId);
  if (addGroupNames) qs.set("addGroupNames", "true");
  appendIfPresent(qs, "groupId", groupId);
  appendIfPresent(qs, "assetTypes", assetTypes);
  return `?${qs.toString()}`;
}

// Build the params string for useGetPendingGlobalQuery. Mirrors the
// same omit-empty semantics so the BE never sees `&groupId=` or
// `&serviceId=undefined`.
export function buildPendingCountParams({ serviceId, groupId }) {
  const qs = new URLSearchParams();
  appendIfPresent(qs, "serviceId", serviceId);
  appendIfPresent(qs, "groupId", groupId);
  return `?${qs.toString()}`;
}

// Parse the params string returned by RTK Query's `originalArgs.params`
// into the normalised fields needed by the schedules-success effect's
// arg-drift guard. Returns `null` if the input is falsy.
//
// Normalisation:
//   - serviceId / groupId / assetTypes are returned as "" when absent
//     OR when isMeaningful() rejects them (legacy "null"/"undefined"
//     literal artefacts).
//   - currentPage is returned as a positive integer (defaults to 1 when
//     absent or unparseable).
export function parseControllingGlobalArgs(paramsString) {
  if (!paramsString) return null;
  const qs = new URLSearchParams(paramsString.replace(/^\?/, ""));
  const norm = (k) => (isMeaningful(qs.get(k)) ? qs.get(k) : "");
  const pageRaw = qs.get("currentPage");
  const currentPage = pageRaw ? parseInt(pageRaw, 10) : 1;
  return {
    serviceId: norm("serviceId"),
    currentPage: Number.isFinite(currentPage) ? currentPage : 1,
    groupId: norm("groupId"),
    assetTypes: norm("assetTypes"),
  };
}

// Normalise a live filter value to the same shape parseControllingGlobalArgs
// produces — used on the "current" side of the arg-drift comparison.
// Symmetric with appendIfPresent by virtue of sharing isMeaningful().
export function normaliseFilter(value) {
  return isMeaningful(value) ? String(value) : "";
}
